import { route, param, Route } from 'typesafe-react-router'
import { RouteComponentProps } from 'react-router-dom'
import getKeys from 'utils/getKeys'

export const routes = {
  /**
   * Routes order is important for analytics reporting so change them with
   * caution.
   * e.g. imagine if we change the order of these two routes:
   *
   * WORKSPACE_CREATE: route('workspace', 'create'),
   * WORKSPACE: route('workspace', param('workspaceId')),
   *
   * If WORKSPACE goes before WORKSPACE_CREATE, we will track a different page visit when
   * going to `/workspace/create` because the route will match the WORKSPACE route, being
   * `create` the `workspaceId` parameter.
   */

  /**
   * This app doesn't "own" '/' root path. This path is dedicated to Marketing website.
   * However, it still might be useful to use it in `<Switch>...</ Switch>` routes.
   * Use this path with extreme caution.
   * see https://github.com/sketch-hq/Cloud/issues/2957
   */
  ROOT_LEGACY: route(''),
  ENTRY: route('c'),
  SIGN_IN: route('signin').withQueryParams('sketch'),
  SIGN_UP: route('signup').withQueryParams('sketch', 'skipWorkspaceCreation'),
  // User stays here until verifies his account
  // New onboarding flow using the 'onboarding-licenses-signup' feature flag
  CHECK_YOUR_INBOX: route('check-your-inbox'),
  ABOUT_YOU: route('about-you').withQueryParams('skipWorkspaceCreation'),
  FORGOT_PASSWORD: route('forgot'),
  RESET_PASSWORD: route('reset'),
  ACCEPT: route('accept').withQueryParams('token'), // Route used on the email verification token
  CONFIRM: route('confirm').withQueryParams('token'), // Route used when changing an existing email
  SSO_SIGN_IN: route('signin', 'sso').withQueryParams('source', 'team'),
  SSO_ERROR: route('signin', 'sso', 'error').withQueryParams(
    'teamName',
    'message'
  ),
  SSO_SUCCESSFUL_SIGN_IN: route('signin', 'sso', 'success').withQueryParams(
    'token',
    'source',
    'from'
  ),
  SSO_LINK_ACCOUNT: route(
    'signin',
    'sso',
    'link',
    param('workspaceId')
  ).withQueryParams('source', 'from'),
  SSO_TOS: route('signin', 'sso', 'tos', param('workspaceId')).withQueryParams(
    'source',
    'from'
  ),
  /** View all Artboards of a single page within a document */
  SHARE_VIEW: route('s', param('shareID')),
  SHARE_PAGE_VIEW: route('s', param('shareID'), 'p', param('pageUUID')),
  SHARE_PAGE_CANVAS_VIEW: route(
    's',
    param('shareID'),
    'p',
    param('pageUUID'),
    'canvas'
  ),
  SHARE_INVITE: route('s', param('shareID'), 'invite'),
  /** Email entry point to accept invite */
  SHARE_ACCEPT_INVITE_ROOT: route('share-invite'),
  SHARE_ACCEPT_INVITE: route('share-invite', 'accept').withQueryParams('token'),
  SHARE_ACCEPT_INVITE_SIGN_UP: route('share-invite', 'signup'),
  SHARE_ACCEPT_INVITE_SIGN_IN: route('share-invite', 'signin'),
  SHARE_ACCEPT_INVITE_FORGOT_PASSWORD: route('share-invite', 'forgot'),
  /** Email entry point to unsubscribe to a share */
  SHARE_UNSUBSCRIBE: route('s', param('shareID'), 'unsubscribe'),
  /** Email entry point to accept share access request */
  SHARE_ACCEPT_ACCESS_REQUEST: route('share-access-request', 'accept'),

  PERSONAL_SETTINGS: route('settings'),
  /** Email entry point to unsubscribe about comment activity */
  PERSONAL_SETTINGS_UNSUBSCRIBE: route('settings', 'unsubscribe'),
  PERSONAL_SETTINGS_API_KEYS: route('settings', 'api-keys'),

  // Confirmation page to disable MFA (MultiFactor Authentcation A.K.A 2FA)
  PERSONAL_SETTINGS_DISABLE_MFA: route('settings', 'disable-mfa'),
  PERSONAL_SETTINGS_CONFIRM_DISABLE_MFA: route(
    'settings',
    'disableMfa'
  ).withQueryParams('token'),

  /* Workspace routes */
  WORKSPACE_CREATE: route('workspace', 'create').withQueryParams(
    'name',
    'upgrade'
  ),
  WORKSPACE_CREATE_DONE: route('workspace', 'create', param('workspaceId')),
  WORKSPACE_INVITE: route('workspace', 'invite').withQueryParams('token'),
  WORKSPACE_LICENSE_REDEEM_CONFIRM: route(
    'workspace',
    'redeem-license'
  ).withQueryParams('token'),
  WORKSPACE: route('workspace', param('workspaceId')),
  WORKSPACE_SHARES: route(
    'workspace',
    param('workspaceId'),
    'shares'
  ).withQueryParams('search'),
  WORKSPACE_SHARED_WITH_ME: route(
    'workspace',
    param('workspaceId'),
    'shared-with-me'
  ),
  WORKSPACE_LIBRARIES: route(
    'workspace',
    param('workspaceId'),
    'libraries'
  ).withQueryParams('search'),
  WORKSPACE_TRASH: route(
    'workspace',
    param('workspaceId'),
    'trash'
  ).withQueryParams('search'),
  WORKSPACE_TRASH_PROJECT: route(
    'workspace',
    param('workspaceId'),
    'trash',
    'p',
    param('projectId')
  ).withQueryParams('search'),
  WORKSPACE_DRAFTS: route(
    'workspace',
    param('workspaceId'),
    'drafts'
  ).withQueryParams('search'),
  WORKSPACE_PROJECT: route(
    'workspace',
    param('workspaceId'),
    'p',
    param('projectId')
  ).withQueryParams('search'),
  // Workspace agnostic routes
  WORKSPACE_AGNOSTIC_SETTINGS: route('workspace', 'settings'),
  WORKSPACE_AGNOSTIC_SETTINGS_GENERAL: route(
    'workspace',
    'settings',
    'general'
  ),
  WORKSPACE_AGNOSTIC_SETTINGS_BILLING: route(
    'workspace',
    'settings',
    'billing'
  ),
  WORKSPACE_AGNOSTIC_SETTINGS_SSO: route('workspace', 'settings', 'sso'),

  WORKSPACE_SETTINGS: route('workspace', param('workspaceId'), 'settings'),
  WORKSPACE_SETTINGS_GENERAL: route(
    'workspace',
    param('workspaceId'),
    'settings',
    'general'
  ),
  WORKSPACE_SETTINGS_BILLING: route(
    'workspace',
    param('workspaceId'),
    'settings',
    'billing'
  ),
  WORKSPACE_SETTINGS_SSO: route(
    'workspace',
    param('workspaceId'),
    'settings',
    'sso'
  ),
  WORKSPACE_SUBSCRIBE: route('workspace', param('workspaceId'), 'subscribe'),

  // All user notifications will now be show on this route
  UPDATES: route('inbox'),

  ARTBOARD_DETAIL: route(
    's',
    param('shareID'),
    'a',
    param('permanentArtboardShortId')
  ),

  // Access an artboard using its uuid and redirect to the equivalent ARTBOARD_DETAIL URL.
  ARTBOARD_DETAIL_UUID: route(
    's',
    param('shareID'),
    'a',
    'uuid',
    param('artboardUUID')
  ),

  ARTBOARD_REVISION: route(
    's',
    param('shareID'),
    'a',
    param('permanentArtboardShortId'),
    'r',
    param('revisionDocShortId')
  ),
  PROTOTYPE: route(
    's',
    param('shareID'),
    'a',
    param('permanentArtboardShortId'),
    'play'
  ),

  /**
   * Backwards compatible route, now redirects to /s/<shareID>/a/<permanentArtboardShortId>/
   * https://github.com/sketch-hq/Cloud/issues/280#issuecomment-484824256
   */
  LEGACY_ARTBOARD_DETAIL: route(
    's',
    param('shareID'),
    param('artboardShortId')
  ),
  /**
   * Backwards compatible route, now redirects to /s/<shareID>/a/<permanentArtboardShortId>/play
   * https://github.com/sketch-hq/Cloud/issues/280#issuecomment-484824256
   * */
  LEGACY_PROTOTYPE: route(
    's',
    param('shareID'),
    param('artboardShortId'),
    'play'
  ),
  LEGACY_ARTBOARD_SLUG: route(
    's',
    param('shareID'),
    param('scope'),
    param('pageSlug'),
    param('artboardSlug')
  ),
  LEGACY_PROTOTYPE_SLUG: route(
    's',
    param('shareID'),
    param('scope'),
    param('pageSlug'),
    param('artboardSlug'),
    'play'
  ),

  /* Legacy BabyCloud Keys */
  MY_DOCUMENTS: route('documents', 'my-documents'),
  SHARED_WITH_ME: route('documents', 'shared-with-me'),
  ALL_DOCUMENTS: route('documents', 'all-documents'),
  LIBRARIES: route('documents', 'libraries'),
  TRASH: route('documents', 'trash'),
  PERSONAL_DOCUMENTS: route('documents'),
  PERSONAL_PROJECT: route('documents', 'p', param('projectId')),
  DOCUMENTS: route('documents', param('filter')),

  /* Legacy Team routes */
  TEAMS_LEGACY: route('teams'),
  TEAM_CREATE: route('team', 'create').withQueryParams(
    'extraSeats',
    'teamName'
  ),
  TEAM_INVITE: route('team', 'invite').withQueryParams('token'),
  TEAM_LICENSE_REDEEM_CONFIRM: route('team', 'redeem-license'),
  TEAM: route('team', param('teamShortId')),
  TEAM_SETTINGS: route('team', param('teamShortId'), 'settings'),
  TEAM_SHARES: route('team', param('teamShortId'), 'shares').withQueryParams(
    'search'
  ),
  TEAM_SHARED_WITH_ME: route('team', param('teamShortId'), 'shared-with-me'),
  TEAM_LIBRARIES: route(
    'team',
    param('teamShortId'),
    'libraries'
  ).withQueryParams('search'),
  TEAM_DRAFTS: route('team', param('teamShortId'), 'drafts').withQueryParams(
    'search'
  ),
  TEAM_TRASH: route('team', param('teamShortId'), 'trash').withQueryParams(
    'search'
  ),
  TEAM_PROJECT: route(
    'team',
    param('teamShortId'),
    'p',
    param('projectId')
  ).withQueryParams('search'),

  // Design system manager
  CWV_SYMBOLS: route('s', param('shareID'), 'symbols'),
  CWV_TEXT_STYLES: route('s', param('shareID'), 'text-styles'),
  CWV_LAYER_STYLES: route('s', param('shareID'), 'layer-styles'),
  CWV_COLOR_VARIABLES: route('s', param('shareID'), 'color-variables'),
}

export const versionedRoutes = {
  SHARE_VIEW: {
    LATEST: routes.SHARE_VIEW,
    VERSION: route('s', param('shareID'), 'v', param('versionShortId')),
  },
  /** View all Artboards of a single page within a document */
  SHARE_PAGE_VIEW: {
    LATEST: routes.SHARE_PAGE_VIEW,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'p',
      param('pageUUID')
    ),
  },
  SHARE_PAGE_CANVAS_VIEW: {
    LATEST: routes.SHARE_PAGE_CANVAS_VIEW,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'p',
      param('pageUUID'),
      'canvas'
    ),
  },
  ARTBOARD_DETAIL: {
    LATEST: routes.ARTBOARD_DETAIL,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'a',
      param('permanentArtboardShortId')
    ),
  },
  ARTBOARD_DETAIL_UUID: {
    LATEST: routes.ARTBOARD_DETAIL_UUID,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'a',
      'uuid',
      param('artboardUUID')
    ),
  },
  ARTBOARD_REVISION: {
    LATEST: routes.ARTBOARD_REVISION,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'a',
      param('permanentArtboardShortId'),
      'r',
      param('revisionDocShortId')
    ),
  },
  PROTOTYPE: {
    LATEST: routes.PROTOTYPE,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'a',
      param('permanentArtboardShortId'),
      'play'
    ),
  },
  CWV_SYMBOLS: {
    LATEST: routes.CWV_SYMBOLS,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'symbols'
    ),
  },
  CWV_TEXT_STYLES: {
    LATEST: routes.CWV_TEXT_STYLES,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'text-styles'
    ),
  },
  CWV_LAYER_STYLES: {
    LATEST: routes.CWV_LAYER_STYLES,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'layer-styles'
    ),
  },
  CWV_COLOR_VARIABLES: {
    LATEST: routes.CWV_COLOR_VARIABLES,
    VERSION: route(
      's',
      param('shareID'),
      'v',
      param('versionShortId'),
      'color-variables'
    ),
  },
}
export type VersionedRoutes = typeof versionedRoutes
export type VersionedRouteKeys = keyof VersionedRoutes
export const versionedRouteKeys = Object.keys(
  versionedRoutes
) as VersionedRouteKeys[]

export type RouteKeys = keyof typeof routes

type RouteCreateFn<TKey extends RouteKeys> = typeof routes[TKey]['create']

export type RouteParam<TKey extends RouteKeys> = Parameters<
  RouteCreateFn<TKey>
>[0]

export type RouteParams<TKey extends RouteKeys> = {
  [key in keyof RouteParam<TKey>]: string
}

/**
 * @example
 * // usage example if only a single route is used for a single component:
 * const Component: FC<RouteProps<'ARTBOARD_REVISION'>>
 *
 * @example
 * // Usage example if multiple routes uses the same component:
 * type ArtboardRouteProps = RouteComponentProps<
 *     Partial<RouteParams<'ARTBOARD_REVISION'>> & RouteParams<'ARTBOARD_DETAIL'>
 *   >
 * const Component: FC<ArtboardRouteProps>
 *
 */
export type RouteProps<TKey extends RouteKeys> = RouteComponentProps<
  RouteParams<TKey>
>

export type QueryParams<TKey extends RouteKeys> = NonNullable<
  RouteParam<TKey>['query']
>

/** Versioned routes */
type VersionedRouteCreateFn<
  TKey extends VersionedRouteKeys
> = typeof versionedRoutes[TKey]['VERSION']['create']

export type VersionedRouteParam<TKey extends VersionedRouteKeys> = Parameters<
  VersionedRouteCreateFn<TKey>
>[0]
export type VersionedRouteParams<TKey extends VersionedRouteKeys> = {
  [key in keyof VersionedRouteParam<TKey>]: string
}
export type VersionedRouteProps<
  TKey extends VersionedRouteKeys
> = RouteComponentProps<VersionedRouteParams<TKey>>

/**
 * @deprecated use versionedRoutes constant instead
 */
export const createGetVersionedRoute = (routes: VersionedRoutes) => (
  key: keyof VersionedRoutes,
  latest = true
) => {
  const latestOrVersionKey = latest ? 'LATEST' : 'VERSION'

  if (routes[key] && routes[key][latestOrVersionKey]) {
    return routes[key][latestOrVersionKey]
  }

  // This part actually never happens in the code,
  // and shouldn't be allowed in the first place
  // the only case where this is triggered is within unit tests
  // anyway, leaving it as it is for backwards compatibility
  if (routes[key]) {
    return (routes[key] as any) as Route<any>
  }

  throw new Error(`No route found for key: ${key}`)
}

/**
 * @deprecated use versionedRoutes constant instead
 */
export const getVersionedRoute = createGetVersionedRoute(versionedRoutes)

export const getAllTemplatesForVersionedRoute = (key: VersionedRouteKeys) => {
  return [
    getVersionedRoute(key, true),
    getVersionedRoute(key, false),
  ].map(route => route.template())
}

export const getAllVersionedRouteTemplates = () =>
  getKeys(versionedRoutes).map(getAllTemplatesForVersionedRoute).flat()

/**
 * getRouteTemplates
 *
 * Return a list with all routes templates in string format.
 * e.g.:
 *
 * ```
 * const templates = getRouteTemplates({ SHARE_VIEW: route('s', param('shareID')) })
 * console.log(templates) // ['s/:shareID']
 * ```
 *
 */
export const getRouteTemplates = (
  appRoutes: typeof routes = routes
): string[] => Object.values(appRoutes).map(value => value.template())

export const getRouteTemplate = (key: RouteKeys): string =>
  routes[key].template()
